<script lang="ts">
  /**
   * 暗記カード（card-schema.md §3.9）。問い（recall.question）に答えてから、答え・statement・snippet・why を見る。
   * - recall.accept なし: 「答えを見る」→「思い出せた」「思い出せなかった」（同じ大きさ・同じ見た目）
   * - recall.accept あり: 入力欄で照合（fill-blank と同じ規則）。入力前に「答えを見る」なら不正解
   * Leitner に載る（記録は親のセッションが行う）。外枠は CardShell と同じ並び（CardShell は Card 型だけを受けるため自前で持つ）。
   */
  import { tick } from 'svelte';
  import CodeBlock from '../CodeBlock.svelte';
  import InlineMarkdown from '../InlineMarkdown.svelte';
  import KeyboardHints, { type KeyHint } from '../KeyboardHints.svelte';
  import VerdictBadge from '../VerdictBadge.svelte';
  import { hasCommandModifier, isEditableTarget } from '../keyboard';
  import type { GradeResult } from '../types';
  import {
    MEMO_FORGOT_LABEL,
    MEMO_RECALLED_LABEL,
    MEMO_REVEAL_LABEL,
    memoRecallMode,
    type MemoAnswer,
    type MemoCard,
  } from '../../lib/cards';
  import { memoGradeResult } from '../lesson/memo-grade';
  import type { MemoCardAnswer } from './answer';

  interface Props {
    card: MemoCard;
    index: number;
    total: number;
    /** Leitner の箱。画面には出さない（problem-first.md 追記。呼び出し側の互換のために受け取るだけ） */
    box?: number;
    result?: GradeResult | undefined;
    answer?: MemoCardAnswer | undefined;
    showUseCase?: boolean;
    ongrade?: ((result: GradeResult, answer: MemoCardAnswer) => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onabort?: (() => void) | undefined;
  }

  // eslint-disable-next-line svelte/no-unused-props
  let {
    card,
    index,
    total,
    result,
    answer: savedAnswer,
    showUseCase = false,
    ongrade,
    onnext,
    onprev,
    onabort,
  }: Props = $props();

  const uid = $props.id();
  const titleId = `${uid}-q`;
  const mode = $derived(memoRecallMode(card).kind === 'typed' ? 'recall' : 'self');

  let text = $state('');
  /** 自己申告で「答えを見る」を押した */
  let shownSelf = $state(false);
  let lastKey = '';
  $effect.pre(() => {
    const key = `${card.id}:${index}`;
    if (key === lastKey) return;
    lastKey = key;
    text = savedAnswer?.text ?? '';
    shownSelf = savedAnswer !== undefined;
  });

  const graded = $derived(result !== undefined);
  const revealed = $derived(graded || shownSelf);
  const isLast = $derived(index >= total);

  let article: HTMLElement | undefined = $state();
  let nextButton: HTMLButtonElement | undefined = $state();
  let firstSelfButton: HTMLButtonElement | undefined = $state();

  let wasGraded = false;
  $effect(() => {
    const now = graded;
    if (now && !wasGraded) void tick().then(() => nextButton?.focus());
    wasGraded = now;
  });

  function emit(a: MemoAnswer) {
    if (graded) return;
    ongrade?.(memoGradeResult(card, a), { kind: 'memo', answer: a, text });
  }

  function submitRecall() {
    if (mode !== 'recall' || graded || text.trim() === '') return;
    emit({ kind: 'typed', input: text });
  }

  function reveal() {
    if (graded) return;
    if (mode === 'recall') {
      emit({ kind: 'revealed' });
      return;
    }
    shownSelf = true;
    void tick().then(() => firstSelfButton?.focus());
  }

  function selfReport(remembered: boolean) {
    emit({ kind: 'self-report', recalled: remembered });
  }

  function onInputKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.isComposing) {
      e.preventDefault();
      e.stopPropagation();
      submitRecall();
    }
  }

  function onkeydown(e: KeyboardEvent) {
    if (e.defaultPrevented || e.isComposing) return;
    const editing = isEditableTarget(e.target);
    if (e.key === 'Escape') {
      e.preventDefault();
      if (editing) {
        if (e.target instanceof HTMLElement) e.target.blur();
        article?.querySelector<HTMLElement>('h1')?.focus();
      } else onabort?.();
      return;
    }
    if (hasCommandModifier(e) || editing) return;
    const target = e.target instanceof Element ? e.target : null;
    const onControl = target?.closest('button, a, summary, [role="button"]') != null;
    if (e.key === 'Enter') {
      if (onControl) return;
      e.preventDefault();
      if (graded) onnext?.();
      else if (mode === 'self' && !shownSelf) reveal();
      else if (mode === 'recall') submitRecall();
      return;
    }
    if (e.key === 'ArrowRight' && graded) {
      e.preventDefault();
      onnext?.();
      return;
    }
    if (e.key === 'ArrowLeft' && index > 1) {
      e.preventDefault();
      onprev?.();
    }
  }

  const keyHints = $derived<KeyHint[]>([
    ...(graded
      ? [{ keys: ['→'], label: isLast ? '結果へ' : '次へ' }]
      : mode === 'self' && !shownSelf
        ? [{ keys: ['Enter'], label: '答えを見る' }]
        : mode === 'recall'
          ? [{ keys: ['Enter'], label: '採点' }]
          : []),
    ...(index > 1 ? [{ keys: ['←'], label: '前のカード' }] : []),
    { keys: ['Esc'], label: '中断' },
  ]);
</script>

<svelte:window {onkeydown} />

<article
  bind:this={article}
  class="card"
  class:graded-ac={graded && result?.verdict === 'AC'}
  class:graded-other={graded && result?.verdict !== 'AC'}
  aria-labelledby={titleId}
  data-testid="card"
  data-card-id={card.id}
  data-state={graded ? 'graded' : text !== '' || shownSelf ? 'answering' : 'idle'}
>
  <div class="card-meta">
    <span class="kind">暗記</span>
    <span>Stage {card.stage}</span>
  </div>

  {#if showUseCase && card.useCase}
    <p class="use-case"><span class="eyebrow">こんなとき</span> {card.useCase}</p>
  {/if}

  <p class="memo-title">{card.title}</p>
  <h1 id={titleId} tabindex="-1"><InlineMarkdown text={card.recall.question} /></h1>

  {#if mode === 'recall'}
    <div class="recall">
      <label class="sub" for="{uid}-answer">答え</label>
      <input
        id="{uid}-answer"
        type="text"
        class="recall-input"
        spellcheck="false"
        autocapitalize="off"
        autocomplete="off"
        data-testid="memo-input"
        bind:value={text}
        readonly={graded}
        aria-invalid={graded && result?.verdict !== 'AC' ? 'true' : undefined}
        onkeydown={onInputKeydown}
      />
    </div>
  {/if}

  {#if !graded}
    <div class="actions">
      {#if mode === 'recall'}
        <button
          class="btn btn-primary"
          type="button"
          data-testid="submit"
          disabled={text.trim() === ''}
          onclick={submitRecall}>採点する <kbd>Enter</kbd></button
        >
        <button class="btn btn-quiet" type="button" data-testid="memo-reveal" onclick={reveal}
          >{MEMO_REVEAL_LABEL}</button
        >
      {:else if !shownSelf}
        <button class="btn btn-primary" type="button" data-testid="memo-reveal" onclick={reveal}
          >{MEMO_REVEAL_LABEL} <kbd>Enter</kbd></button
        >
      {:else}
        <div class="self" role="group" aria-label="思い出せたか">
          <button
            bind:this={firstSelfButton}
            class="btn self-btn"
            type="button"
            data-testid="memo-remembered"
            onclick={() => selfReport(true)}>{MEMO_RECALLED_LABEL}</button
          >
          <button
            class="btn self-btn"
            type="button"
            data-testid="memo-forgot"
            onclick={() => selfReport(false)}>{MEMO_FORGOT_LABEL}</button
          >
        </div>
      {/if}
    </div>
  {/if}

  {#if revealed}
    <section class="reveal" aria-label="答え" data-testid="memo-answer">
      <h2 class="sub">答え</h2>
      <p class="answer"><InlineMarkdown text={card.recall.answer} /></p>
      <div class="statement"><InlineMarkdown text={card.statement} block /></div>
      {#if card.snippet}
        <CodeBlock code={card.snippet.replace(/\n$/, '')} label="{card.title} のコード" />
      {/if}
      {#if card.why}
        <div class="why">
          <span class="eyebrow">理由</span>
          <InlineMarkdown text={card.why} block />
        </div>
      {/if}
    </section>
  {/if}

  <div
    class="feedback"
    class:empty={!graded}
    role="status"
    aria-live="polite"
    data-testid="feedback"
  >
    {#if graded && result}
      <VerdictBadge verdict={result.verdict} />
      {#if result.message}<p>{result.message}</p>{/if}
      <div class="actions">
        <button
          bind:this={nextButton}
          class="btn btn-primary"
          type="button"
          data-testid="next"
          onclick={() => onnext?.()}
        >
          {isLast ? '結果を見る' : '次へ'} <kbd>→</kbd>
        </button>
      </div>
    {/if}
  </div>
</article>

<KeyboardHints hints={keyHints} />

<style>
  .card {
    background: var(--surface);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-card);
    padding: var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    border: 2px solid transparent;
    min-width: 0;
  }
  .card.graded-ac {
    border-color: var(--verdict-ac);
  }
  .card.graded-other {
    border-color: var(--border-strong);
  }
  .card-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    flex-wrap: wrap;
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .card-meta .kind {
    color: var(--accent-soft-text);
    background: var(--accent-soft);
    padding: 0 var(--space-2);
    border-radius: var(--radius-xs);
    font-weight: var(--fw-semibold);
  }
  .use-case {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .use-case .eyebrow {
    margin-right: var(--space-2);
  }
  .memo-title {
    font-size: var(--fs-sm);
    font-weight: var(--fw-semibold);
    color: var(--text-soft);
    margin-bottom: calc(-1 * var(--space-2));
  }
  h1 {
    font-size: var(--fs-lg);
    line-height: var(--lh-ui);
  }
  .sub {
    display: block;
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .recall-input {
    font-family: var(--font-mono);
    font-size: var(--fs-code);
    width: 100%;
  }
  .actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .self {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-3);
    width: 100%;
  }
  .self-btn {
    width: 100%;
  }
  .reveal {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-left: 4px solid var(--accent);
    background: var(--canvas);
    border-radius: 0 var(--radius-sm) var(--radius-sm) 0;
    min-width: 0;
  }
  .answer {
    font-weight: var(--fw-semibold);
  }
  .why {
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .why .eyebrow {
    display: block;
    margin-bottom: var(--space-1);
  }
  .feedback {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding-top: var(--space-4);
    border-top: 1px solid var(--border);
  }
  .feedback.empty {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    border: 0;
  }
  @media (max-width: 639px) {
    .card {
      padding: var(--space-4);
    }
  }
</style>
